import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	DKLabel,
	DKButton,
	DKInput,
	INPUT_TYPE,
	INPUT_VIEW_DIRECTION,
	showLoader,
	removeLoader,
	showToast,
	TOAST_TYPE,
} from "deskera-ui-library";
import PolicyAndResourcesService from "./PolicyAndResourcesService";
import FileUploadField from "../../SharedComponents/FileUploadField";
import { getRandomHexString } from "../../utility/Utility";
import { Entity } from "../../services/AttachmentService";
import { CANDIDATE_ACCEPT_FILE_TYPE } from "../../constants/Constant";

const AddNewDocument = (props) => {
	const { t } = useTranslation();

	const initialFormState = {
		name: {
			id: "name",
			title: t("Name"),
			type: INPUT_TYPE.TEXT,
			validation: [["required"]],
		},
		description: {
			id: "description",
			title: t("Description"),
			type: INPUT_TYPE.LONG_TEXT,
			validation: [["required"]],
		},
		path: {
			id: "path",
			title: t("Document"),
			type: INPUT_TYPE.FILE,
            inputText: "Choose a file",
            acceptText: "Choose pdf, doc, ppt, image, xls, csv etc.",
            accept: CANDIDATE_ACCEPT_FILE_TYPE.join(),
			validation: [["required"]],
		},
	};

	const [formState, setFormState] = useState(initialFormState);

	const isRequired = (validation) => {
		return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
	};

	const hasError = (field, value) => {
		let error = {
			invalid: false,
			errorMessage: null,
		};

		const validation = field.validation;

		if (validation && validation.length !== 0) {
			validation.forEach((validation) => {
				switch (validation[0]) {
					case "required":
						if (value === null || value === undefined || String(value).trim() === "") {
							error.invalid = true;
						}
						break;
					default:
						error.invalid = false;
						error.errorMessage = null;
						break;
				}
			});
		}

		return error;
	};

	const updateState = (stateName, field, value) => {
		const newState = { ...formState };
		newState[stateName] = { ...field, value: value, ...hasError(field, value) };

		setFormState(newState);
	};

    const isInvalid = () => {
        let invalid = false;

        const newState = { ...formState };
        Object.entries(newState).forEach(([key, field]) => {
            const error = hasError(field, field.value);
            field.invalid = error.invalid;
            field.errorMessage = error.errorMessage;

            if (field.invalid) {
                invalid = true;
            }
        });

        setFormState(newState);

        return invalid;
    }

    const getPayload = () => {
        const payload = {
			data: [
				{
					name: formState.name.value?.trim(),
					description: formState.description.value?.trim(),
					path: formState.path.value,
				},
			],
		};

        return payload;
    }

	const saveDocument = () => {
		if (isInvalid()) {
			return;
		}

		const payload = getPayload();
		showLoader();
        PolicyAndResourcesService.savePolicyAndResourceDocument(payload).then(
            (res) => {
                showToast("Document added successfully", TOAST_TYPE.SUCCESS);
                removeLoader();
                props.onClose(true);
            },
            (error) => {
                removeLoader();
                const message = error?.message || t("SOMETHING_WENT_WRONG");
                showToast(message, TOAST_TYPE.FAILURE);
            }
        );
	};

	const renderField = (stateName, field, width, className) => {
		switch (field.type) {
			case INPUT_TYPE.TEXT:
            case INPUT_TYPE.LONG_TEXT:
				return (
					<div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
						<div className={`row position-relative ${
								!className ? "pb-l mb-s" : className
							}`}
						>
							<DKInput
								ref={field.ref}
								title={field.title}
								type={field.type}
								direction={INPUT_VIEW_DIRECTION.VERTICAL}
								value={field.value}
								errorMessage={field.errorMessage}
								readOnly={!!field.readOnly}
								required={isRequired(field.validation)}
								tooltip={{
									content: field.tooltip,
									className: "popup-tooltip-prop",
								}}
								invalid={field.invalid}
								canValidate={field.invalid}
								onChange={(value) => {
									updateState(stateName, field, value);
								}}
								validator={field.validator}
							/>
						</div>
					</div>
				);
            case INPUT_TYPE.FILE:
                return (
					<div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
						<div className={`row position-relative ${
								!className ? "pb-l mb-s" : className
							}`}
						>
                            <FileUploadField
                                fieldId={getRandomHexString(6)}
                                title={field.title}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                inputText={field.inputText}
                                acceptText={field.acceptText}
                                accept={field.accept}
                                entity={Entity.PEOPLE_DOCUMENT}
                                onChange={(relativePath) => {
                                    updateState(stateName, field, relativePath);
                                }}
                            />
                        </div>
					</div>
				);
			default:
				break;
		}
	};

	return (
		<div className="transparent-background">
			<div className="popup-window" style={{ padding: 0, overflow: "hidden" }}>
				<div className="parent-width" style={{ height: "60vh" }}>
					<div className="row p-h-xl pt-xl pb-m">
						<DKLabel text={t("Add New Document")} className="fs-l fw-b" />
						<div className="ml-auto d-flex">
							<DKButton
								title={t("CANCEL")}
								className="border-radius-m bg-gray1 border-m"
								onClick={() => {
									props.onClose();
								}}
							/>
							<DKButton
								title={t("SAVE")}
								className="bg-app text-white ml-m"
								onClick={() => {
									saveDocument();
								}}
							/>
						</div>
					</div>
					<div
						className="parent-width custom-scroll"
						style={{ overflow: "auto", height: "calc(100% - 69px)" }}
					>
						<div className="p-h-xl p-v-m">
							{renderField("name", formState.name, "100%")}
							{renderField("description", formState.description, "100%")}
                            {renderField("path", formState.path, "100%")}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AddNewDocument;
